import { useCallback, useMemo, useState } from 'react';

import {
  ErrorResponse,
  SubscriptionStatus,
  Tariff,
  TariffFeature,
  TariffLimitResource,
  TariffLimits,
  TariffLimitUsage,
  TariffUpgradeReason,
  TARIFF_LIMIT_ERROR_CODES,
} from 'src/types';
import { IS_LOCALHOST } from 'src/config-global';

// ----------------------------------------------------------------------

export type TariffData = {
  tariff: Tariff | null;
  tariffLimits: TariffLimits;
  canUseFeature: (feature: TariffFeature) => boolean;
  getLimit: (resource: TariffLimitResource) => TariffLimitUsage | null;
  isAtLimit: (resource: TariffLimitResource, currentCount?: number) => boolean;
  guardFeature: (feature: TariffFeature) => boolean;
  guardLimit: (resource: TariffLimitResource, currentCount?: number) => boolean;
  handleTariffApiError: (error: ErrorResponse) => boolean;
  upgradeDialogOpen: boolean;
  upgradeReason: TariffUpgradeReason | null;
  openUpgradeDialog: (reason: TariffUpgradeReason) => void;
  closeUpgradeDialog: () => void;
};

// Prefers the new tariff block; falls back to the legacy `usage` field
// until the backend serves limits through `tariff.limits`.
// FIXME: remove fallback once the backend is rolled out to all tenants.
function resolveTariffLimits(status: SubscriptionStatus | null): TariffLimits {
  if (!status) return {};
  if (status.tariff?.limits) return status.tariff.limits;

  // This IS the fallback, so reading the deprecated field is the point.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { students, members } = status.usage ?? {};

  return {
    ...(students ? { students } : {}),
    ...(members ? { members } : {}),
  };
}

// ----------------------------------------------------------------------

export const useTariffData = (status: SubscriptionStatus | null): TariffData => {
  const [upgradeReason, setUpgradeReason] = useState<TariffUpgradeReason | null>(null);

  const tariff = status?.tariff ?? null;
  const tariffLimits = useMemo(() => resolveTariffLimits(status), [status]);

  const openUpgradeDialog = useCallback((reason: TariffUpgradeReason) => {
    setUpgradeReason(reason);
  }, []);

  const closeUpgradeDialog = useCallback(() => {
    setUpgradeReason(null);
  }, []);

  const canUseFeature = useCallback(
    (feature: TariffFeature): boolean => {
      if (IS_LOCALHOST) return true;
      if (!tariff) return true;
      return !tariff.disabled_features?.includes(feature);
    },
    [tariff]
  );

  const getLimit = useCallback(
    (resource: TariffLimitResource): TariffLimitUsage | null => tariffLimits[resource] ?? null,
    [tariffLimits]
  );

  const isAtLimit = useCallback(
    (resource: TariffLimitResource, currentCount?: number): boolean => {
      if (IS_LOCALHOST) return false;
      const usage = tariffLimits[resource];
      if (!usage || usage.limit === null) return false;
      return (currentCount ?? usage.current) >= usage.limit;
    },
    [tariffLimits]
  );

  const guardFeature = useCallback(
    (feature: TariffFeature): boolean => {
      if (canUseFeature(feature)) return true;
      openUpgradeDialog({ type: 'feature', feature });
      return false;
    },
    [canUseFeature, openUpgradeDialog]
  );

  const guardLimit = useCallback(
    (resource: TariffLimitResource, currentCount?: number): boolean => {
      if (!isAtLimit(resource, currentCount)) return true;
      openUpgradeDialog({ type: 'limit', resource, usage: tariffLimits[resource] });
      return false;
    },
    [isAtLimit, openUpgradeDialog, tariffLimits]
  );

  const handleTariffApiError = useCallback(
    (error: ErrorResponse): boolean => {
      if (error.code !== TARIFF_LIMIT_ERROR_CODES.LIMIT_EXCEEDED) return false;
      openUpgradeDialog({ type: 'limit' });
      return true;
    },
    [openUpgradeDialog]
  );

  return useMemo<TariffData>(
    () => ({
      tariff,
      tariffLimits,
      canUseFeature,
      getLimit,
      isAtLimit,
      guardFeature,
      guardLimit,
      handleTariffApiError,
      upgradeDialogOpen: upgradeReason !== null,
      upgradeReason,
      openUpgradeDialog,
      closeUpgradeDialog,
    }),
    [
      tariff,
      tariffLimits,
      canUseFeature,
      getLimit,
      isAtLimit,
      guardFeature,
      guardLimit,
      handleTariffApiError,
      upgradeReason,
      openUpgradeDialog,
      closeUpgradeDialog,
    ]
  );
};
